import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";

import {
  IAssignOrganiserToShowReq,
  ICheckIsOrganiserRelatedToShowReq,
  ICheckIsOrganiserRelatedToShowRes,
  ICheckIsOrganiserShowsAdminQueryReq,
  ICheckIsOrganiserShowsAdminRes,
  ICreateOrganiserCompanyWithOrganiserReq,
  ICreateOrganiserReq,
  ICreateOrganiserToShowReq,
  IDeleteOrganiserQueryReq,
  IGetByCompanyOrganiserQueryReq,
  IGetManyOrganiserByAdminQueryReq,
  IGetManyOrganiserQueryReq,
  IGetOneByEmailOrganiserQueryReq,
  IGetOneByShowIdOrganiserQueryReq,
  IGetOneByUserIdOrganiserQueryReq,
  IGetOneOrganiserQueryReq,
  IGetOrganiserRelatedToShowQueryReq,
  IGetOrganiserShowRoleQueryReq,
  IGetOrganiserShowRoleRes,
  IGetProfileChangeRequestRecipientOrganisersQueryReq,
  IUpdateOrganiserReq,
  IUpdateOrganiserToShowReq,
} from "@evennt-tech/api-v2-external";

import {
  INotificationBaseMessage,
  IOrganiser,
  NotificationType,
  OrganiserToShowRole,
  IPayloadUpdateProfileChangeRecipients,
} from "@evennt-tech/api-v2-core";
import { getErrorMessage } from "tools/error";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  itemsForAdmin: IOrganiser[];
  paginationForAdminItems: PaginationMeta;
  lastUsedAdminParams: IGetManyOrganiserByAdminQueryReq | null;

  itemsForOrganiser: IOrganiser[];
  paginationForOrganiserItems: PaginationMeta;
  lastUsedOrganiserParams: IGetManyOrganiserQueryReq | null;

  itemsByCompany: IOrganiser[];

  itemsRelatedToShow: IOrganiser[];

  selected: IOrganiser | null;

  isLoading: boolean;
  error: string | null;
  requestRecipientIdsRelatedToShow: string[];
  isRequestRecipientsLoading: boolean;
};

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

type Action = {
  fetchMany: (params: IGetManyOrganiserQueryReq) => Promise<void>;

  fetchManyByAdmin: (params: IGetManyOrganiserByAdminQueryReq) => Promise<void>;

  reload: () => void;

  autoCatch: () => void;

  fetchByCompany: (params: IGetByCompanyOrganiserQueryReq) => Promise<void>;

  fetchRelatedToShow: (
    params: IGetOrganiserRelatedToShowQueryReq,
    force?: boolean
  ) => Promise<void>;

  fetchRequestRecipients: (
    params: IGetProfileChangeRequestRecipientOrganisersQueryReq,
    force?: boolean
  ) => Promise<void>;

  autoCatchRequestRecipients: (showId: string) => () => void;

  autoCatchRelatedToShow: (
    params: IGetOrganiserRelatedToShowQueryReq
  ) => () => void;

  getOrganiserShowRole: (
    params: IGetOrganiserShowRoleQueryReq
  ) => Promise<IGetOrganiserShowRoleRes>;

  rolesByShowId: Record<string, OrganiserToShowRole | null>;

  checkIsOrganiserRelatedToShow: (
    params: ICheckIsOrganiserRelatedToShowReq
  ) => Promise<ICheckIsOrganiserRelatedToShowRes>;

  checkIsOrganiserShowsAdmin: (
    params: ICheckIsOrganiserShowsAdminQueryReq
  ) => Promise<ICheckIsOrganiserShowsAdminRes>;

  fetchOne: (params: IGetOneOrganiserQueryReq) => Promise<void>;

  fetchOneByUserId: (params: IGetOneByUserIdOrganiserQueryReq) => Promise<void>;

  fetchOneByShowId: (params: IGetOneByShowIdOrganiserQueryReq) => Promise<void>;

  fetchOneByEmail: (params: IGetOneByEmailOrganiserQueryReq) => Promise<void>;

  createOrganiserCompanyWithOrganiser: (
    body: ICreateOrganiserCompanyWithOrganiserReq
  ) => Promise<void>;

  createOrganiser: (body: ICreateOrganiserReq) => Promise<void>;

  createOrganiserToShow: (body: ICreateOrganiserToShowReq) => Promise<void>;

  assignOrganiserToShow: (body: IAssignOrganiserToShowReq) => Promise<void>;

  edit: (body: IUpdateOrganiserReq) => Promise<void>;

  editOrganiserToShow: (body: IUpdateOrganiserToShowReq) => Promise<void>;

  remove: (params: IDeleteOrganiserQueryReq) => Promise<void>;

  clearSelected: () => void;

  setSelected: (organiser: IOrganiser | null) => void;

  getSelected: () => IOrganiser | null;

  setError: (message: string | null) => void;

  setItemsByCompany: (items: IOrganiser[]) => void;

  resetRelatedToShow: () => void;
};

export const useOrganisers = create<State & Action>()(
  devtools((set, get) => ({
    itemsForAdmin: [] as IOrganiser[],
    paginationForAdminItems: { page: 1, limit: 0, total: 0 },
    lastUsedAdminParams: null as IGetManyOrganiserByAdminQueryReq | null,

    itemsForOrganiser: [] as IOrganiser[],
    paginationForOrganiserItems: { page: 1, limit: 0, total: 0 },
    lastUsedOrganiserParams: null as IGetManyOrganiserQueryReq | null,

    itemsByCompany: [] as IOrganiser[],
    itemsRelatedToShow: [] as IOrganiser[],
    requestRecipientIdsRelatedToShow: [] as string[],
    isRequestRecipientsLoading: false,

    rolesByShowId: {} as Record<string, OrganiserToShowRole | null>,

    selected: null as IOrganiser | null,
    isLoading: false,
    error: null as string | null,

    setError: (message) => set({ error: message }),

    setSelected: (organiser) => set({ selected: organiser }),
    getSelected: () => get().selected,

    setItemsByCompany: (items) =>
      set({
        itemsByCompany: items,
      }),

    fetchMany: async (params) => {
      set({ isLoading: true, lastUsedOrganiserParams: params });
      try {
        const res = await apiV2.organiser.getMany(params);
        const paginationData = {
          page: res.meta.currentPage,
          limit: res.meta.itemsPerPage,
          total: res.meta.totalItems ?? 0,
        };

        set({
          itemsForOrganiser: res.items ?? [],
          paginationForOrganiserItems: paginationData,
          error: null,
        });
      } catch (e) {
        console.error("Failed to fetch organisers:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    fetchManyByAdmin: async (params) => {
      set({ isLoading: true, lastUsedAdminParams: params });
      try {
        const res = await apiV2.organiser.getManyByAdmin(params);
        const paginationData = {
          page: res.meta.currentPage,
          limit: res.meta.itemsPerPage,
          total: res.meta.totalItems ?? 0,
        };

        set({
          itemsForAdmin: res.items ?? [],
          paginationForAdminItems: paginationData,
          error: null,
        });
      } catch (e) {
        console.error("Failed to fetch organisers:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    fetchRequestRecipients: async (params, force = false) => {
      const {
        isRequestRecipientsLoading,
        requestRecipientIdsRelatedToShow,
      } = get();

      if (!force) {
        if (
          isRequestRecipientsLoading ||
          requestRecipientIdsRelatedToShow.length
        )
          return;
      }

      try {
        set({ isRequestRecipientsLoading: true });
        const result = await apiV2.organiser.getExhibitorProfileChangeRequestRecipients(
          params
        );
        set({
          requestRecipientIdsRelatedToShow: result.map((r) => r.organiserId),
        });
      } catch (e) {
        console.error("Error occurred fetching request recipients: ", e);
      } finally {
        set({ isRequestRecipientsLoading: false });
      }
    },
    autoCatchRequestRecipients: (showId) => {
      apiV2.notification.subscribe<
        INotificationBaseMessage<IPayloadUpdateProfileChangeRecipients>
      >({
        key:
          NotificationType.UPDATED_EXHIBITOR_PROFILE_CHANGE_REQUEST_RECIPIENTS,
        callback: async ({ payload }) => {
          if (!payload) return;

          const shouldUpdate = payload.showIds.includes(showId);
          if (shouldUpdate) {
            await get().fetchRequestRecipients({ showId }, true);
          }
        },
      });

      return () =>
        apiV2.notification.unsubscribe({
          key:
            NotificationType.UPDATED_EXHIBITOR_PROFILE_CHANGE_REQUEST_RECIPIENTS,
        });
    },

    reload: async () => {
      const {
        fetchManyByAdmin,
        fetchMany,
        lastUsedAdminParams,
        lastUsedOrganiserParams,
      } = get();

      if (lastUsedAdminParams) {
        await fetchManyByAdmin(lastUsedAdminParams);
      }

      if (lastUsedOrganiserParams) {
        await fetchMany(lastUsedOrganiserParams);
      }
    },

    autoCatch: async () => {
      const { reload } = get();
      const keys = [
        NotificationType.CREATED_ORGANISER,
        NotificationType.UPDATED_ORGANISER,
        NotificationType.DELETED_ORGANISER,
        NotificationType.CREATED_ORGANISER_COMPANY,
        NotificationType.UPDATED_ORGANISER_COMPANY,
        NotificationType.DELETED_ORGANISER_COMPANY,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          callback: () => reload(),
        });
      }
    },

    autoCatchRelatedToShow: (params) => {
      const { fetchRelatedToShow } = get();
      const keys = [
        NotificationType.CREATED_ORGANISER,
        NotificationType.UPDATED_ORGANISER,
        NotificationType.DELETED_ORGANISER,
        NotificationType.CREATED_ORGANISER_COMPANY,
        NotificationType.UPDATED_ORGANISER_COMPANY,
        NotificationType.DELETED_ORGANISER_COMPANY,
        NotificationType.ASSIGNED_ORGANISER,
        NotificationType.UPDATED_ORGANISER_SHOW_ROLE,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          callback: () => fetchRelatedToShow(params, true),
        });
      }

      return () => {
        for (const key of keys) {
          apiV2.notification.unsubscribe({ key });
        }
      };
    },

    fetchByCompany: async (params) => {
      set({ isLoading: true });
      try {
        const { organiserCompanyId } = params;
        const organisers = await apiV2.organiser.getByCompany({
          organiserCompanyId,
        });
        set({ itemsByCompany: organisers, error: null });
      } catch (e) {
        console.error("Failed to fetch organisers:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    fetchRelatedToShow: async (params, force = false) => {
      try {
        const { itemsRelatedToShow } = get();
        if (!force) {
          if (itemsRelatedToShow.length) return;
        }
        const { showId } = params;
        const organisers = await apiV2.organiser.getOrganisersRelatedToShow({
          showId,
        });

        set({ itemsRelatedToShow: organisers, error: null });
      } catch (e) {
        console.error("Failed to fetch organisers:", e);
      }
    },

    clearSelected: () => set({ selected: null }),

    fetchOne: async (params) => {
      try {
        const data = await apiV2.organiser.getOne(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch organiser:", e);
        set({ selected: null });
      }
    },

    fetchOneByUserId: async (params) => {
      try {
        const data = await apiV2.organiser.getOneByUserId(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch organiser:", e);
        set({ selected: null });
      }
    },

    fetchOneByShowId: async (params) => {
      try {
        const data = await apiV2.organiser.getOneByShowId(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch organiser:", e);
        set({ selected: null });
      }
    },

    fetchOneByEmail: async (params) => {
      try {
        const data = await apiV2.organiser.getOneByEmail(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch organiser:", e);
        set({ selected: null });
      }
    },

    createOrganiserCompanyWithOrganiser: async (body) => {
      try {
        const res = await apiV2.organiser.createOrganiserCompanyWithOrganiser(
          body
        );
        set((state) => ({
          itemsForAdmin: [...state.itemsForAdmin, res],
          itemsForOrganiser: [...state.itemsForOrganiser, res],
          error: null,
        }));
      } catch (e) {
        console.error("Failed to create organiser company with organiser:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    getOrganiserShowRole: async (params) => {
      try {
        const { showId } = params;

        const res = await apiV2.organiser.getOrganiserShowRole(params);

        set((state) => ({
          error: null,
          rolesByShowId: {
            ...state.rolesByShowId,
            [showId]: res,
          },
        }));

        return res;
      } catch (e) {
        console.error("Failed to get organiser show role:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    checkIsOrganiserRelatedToShow: async (params) => {
      try {
        const res = await apiV2.organiser.checkIsOrganiserRelatedToShow(params);
        set((state) => ({
          error: null,
        }));

        return res;
      } catch (e) {
        console.error("Failed check is organiser related show:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    checkIsOrganiserShowsAdmin: async (params) => {
      try {
        const res = await apiV2.organiser.checkIsOrganiserShowsAdmin(params);
        set((state) => ({
          error: null,
        }));

        return res;
      } catch (e) {
        console.error("Failed check is organiser shows admin:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    createOrganiser: async (body) => {
      try {
        const res = await apiV2.organiser.createOrganiser(body);
        set((state) => {
          return {
            itemsForAdmin: [...state.itemsForAdmin, res],
            itemsForOrganiser: [...state.itemsForOrganiser, res],
            error: null,
          };
        });
      } catch (e) {
        console.error("Failed to create organiser:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    createOrganiserToShow: async (body) => {
      try {
        const res = await apiV2.organiser.createOrganiserToShow(body);
        set((state) => {
          const exists = state.itemsRelatedToShow.some(
            (item) => item.id === res.id
          );

          return {
            itemsForAdmin: [...state.itemsForAdmin, res],
            itemsRelatedToShow: body?.showId
              ? exists
                ? state.itemsRelatedToShow.map((item) =>
                    item.id === res.id
                      ? { ...res, roleToShow: body.role }
                      : item
                  )
                : [
                    ...state.itemsRelatedToShow,
                    { ...res, roleToShow: body.role },
                  ]
              : state.itemsRelatedToShow,
            error: null,
          };
        });
      } catch (e) {
        console.error("Failed to create organiser:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    edit: async (body) => {
      try {
        const res = await apiV2.organiser.edit(body);
        set((state) => ({
          itemsForAdmin: state.itemsForAdmin.map((item) =>
            item.id === res.id ? res : item
          ),
          itemsForOrganiser: state.itemsForOrganiser.map((item) =>
            item.id === res.id ? res : item
          ),
          itemsRelatedToShow: state.itemsRelatedToShow.map((item) =>
            item.id === res.id ? res : item
          ),
          selected: res,
          error: null,
        }));
      } catch (e) {
        console.error("Failed to edit organiser:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    editOrganiserToShow: async (body) => {
      try {
        const res = await apiV2.organiser.editOrganiserToShow(body);
        set((state) => ({
          itemsForAdmin: state.itemsForAdmin.map((item) =>
            item.id === res.id ? res : item
          ),
          itemsForOrganiser: state.itemsForOrganiser.map((item) =>
            item.id === res.id ? res : item
          ),
          itemsRelatedToShow: state.itemsRelatedToShow.map((item) =>
            item.id === res.id ? res : item
          ),
          error: null,
        }));
      } catch (e) {
        console.error("Failed to edit organiser to show:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    remove: async (params) => {
      try {
        await apiV2.organiser.delete(params);
        set((state) => ({
          itemsForAdmin: state.itemsForAdmin.filter(
            (item) => item.id !== params.organiserId
          ),
          itemsForOrganiser: state.itemsForOrganiser.filter(
            (item) => item.id !== params.organiserId
          ),
          itemsRelatedToShow: state.itemsRelatedToShow.filter(
            (item) => item.id !== params.organiserId
          ),
          error: null,
        }));
      } catch (e) {
        console.error("Failed to delete organiser:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    assignOrganiserToShow: async (body) => {
      try {
        const res = await apiV2.organiser.assignOrganiserToShow(body);

        set((state) => {
          const exists = state.itemsRelatedToShow.some(
            (item) => item.id === res.id
          );

          return {
            itemsForAdmin: state.itemsForAdmin.map((item) =>
              item.id === res.id ? res : item
            ),
            itemsForOrganiser: state.itemsForOrganiser.map((item) =>
              item.id === res.id ? res : item
            ),
            itemsRelatedToShow: exists
              ? state.itemsRelatedToShow.map((item) =>
                  item.id === res.id ? { ...res, roleToShow: body.role } : item
                )
              : [
                  ...state.itemsRelatedToShow,
                  { ...res, roleToShow: body.role },
                ],
            error: null,
          };
        });
      } catch (e) {
        console.error("Failed to assign organiser:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },
    resetRelatedToShow: () => {
      set({
        itemsRelatedToShow: [],
        error: null,
        requestRecipientIdsRelatedToShow: [],
        isRequestRecipientsLoading: false,
      });
    },
  }))
);

registerStore(useOrganisers);
